if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.elements.viewer?.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        // Use the image currently in view, not an older debounced event.
        const slider = this.elements.viewer?.slider;
        let slide = event.detail?.currentElement;

        if (slider) {
          const slides = Array.from(
            slider.querySelectorAll('li[data-media-id]')
          ).filter((item) => item.getClientRects().length);

          const left = slider.getBoundingClientRect().left;

          slide = slides.reduce((nearest, item) => {
            if (!nearest) return item;

            return Math.abs(item.getBoundingClientRect().left - left) <
              Math.abs(nearest.getBoundingClientRect().left - left)
              ? item
              : nearest;
          }, null);
        }

        const mediaKey = slide?.dataset.mediaId;
        if (!mediaKey) return;

        this.setActiveThumbnail(
          this.elements.thumbnails?.querySelector(
            `[data-target="${mediaKey}"]`
          )
        );

        const mapElement = this.querySelector('[data-gallery-variant-map]');
        const productInfo = this.closest('product-info');
        const picker = productInfo?.querySelector('variant-selects');
        if (!mapElement || !picker) return;

        // A direct selection must still be allowed to move the photo.
        if (!this._pickerChangeListener) {
          this._pickerChangeListener = (changeEvent) => {
            if (
              changeEvent.target.closest('variant-selects') &&
              !changeEvent.sfsGallerySwipe
            ) {
              this._swipeMediaRequests?.clear();
            }
          };

          productInfo.addEventListener(
            'change',
            this._pickerChangeListener,
            true
          );
        }

        let variants;
        try {
          variants = JSON.parse(mapElement.textContent);
        } catch {
          return;
        }

        const matches = variants.filter(
          (variant) =>
            variant.mediaId &&
            mediaKey.endsWith(`-${variant.mediaId}`)
        );

        if (matches.length !== 1) return;

        const variant = matches[0];
        if (variant.options.length !== 1) return;

        const optionValue = variant.options[0];
        const input = Array.from(
          picker.querySelectorAll('input[type="radio"]')
        ).find((radio) => radio.value === optionValue);

        let control;

        if (input) {
          if (input.checked || input.disabled) return;
          input.checked = true;
          control = input;
        } else {
          const select = picker.querySelector('select');
          if (!select || select.disabled) return;

          const option = Array.from(select.options).find(
            (item) => item.value === optionValue
          );

          if (!option || option.disabled || option.selected) return;

          select.value = option.value;
          control = select;
        }

        this._swipeMediaRequests ||= new Set();
        this._swipeMediaRequests.add(String(variant.mediaId));

        const changeEvent = new Event('change', { bubbles: true });
        changeEvent.sfsGallerySwipe = true;
        control.dispatchEvent(changeEvent);
      }
      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );

            if (
              activeThumbnail &&
              activeThumbnail.parentElement.firstChild !== activeThumbnail
            ) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft
            });
          }

          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;

          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );

        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition
          );
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));

        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace('[index]', position);

          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');

        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector('sticky-header');

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute('role', 'presentation');

        this.elements.viewer.sliderItems.forEach((slide) => {
          slide.setAttribute('role', 'presentation');
        });
      }
    }
  );
}